/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';

import { ImageService } from "../../../services/image.service";
import { PropertyTypeService } from '../../../services/property-type.service';
import { TagService } from '../../../services/tag.service';
import { ReaderService } from '../../../services/reader.service';
import { DriverObjectService } from '../../../services/driver-object.service';
import { ObjectService } from '../../../services/object.service';
import { AntennaService } from '../../../services/antenna.service';
import { LocationService } from '../../../services/location.service';
import { ServiceService } from '../../../services/service.service';
import { ServerObjectService } from '../../../services/server-object.service';
import { ZoneService } from '../../../services/zone.service';

import { BaseEntity } from '../../../models/base-entity';
import { GroupType } from '../../../models/group-type';
import { EmGroup } from '../../../models/em-group';
import { Property } from '../../../models/property';
import { PropertyType } from '../../../models/property-type';
import { PropertyTypeParameter } from '../../../models/property-type-parameter';
import { CanActivateAuthGuard } from "../../../can-activate.authguard";
import { IconUploadConfirmDialogService } from 'app/services/icon-upload-confirm-dialog-service.service';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from 'app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component';

declare var $: any;

@Component({
  selector: 'app-entity-type-dialog',
  templateUrl: './entity-type-dialog.component.html'
})
export class EntityTypeDialogComponent implements OnInit {

  @ViewChild('entityImage')
  entityImageFileInput: ElementRef;

  @ViewChild('entityIcon')
  entityIconFileInput: ElementRef;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;


  /*
   * Base Inputs used by all Entity Types
   */
  @Input() //Entity being created/edited
  entity: BaseEntity;

  @Input() //Name of the Entity being edited.  Displayed in the Dialog Header
  entityName: string;

  @Input() //Type of Entity being created/edited.  Used to figure out if extra fields need to be shown
  entityType: GroupType;

  @Input() //Name of the Entity Type being created/edited.  Displayed in the Dialog Header
  entityTypeName: string;

  entityParent: BaseEntity;

  /*
   * Base Outputs used by all Entity Types
   */
  @Output() //Event/Method called when the User clicks the CREATE or UPDATE button indicating they want to attempt to save
  createOrUpdateEvent = new EventEmitter<boolean>();

  @Output() //Event/Method called when the User clicks a Group button.  Used to tell the infrastructure page to display the Group Dialog for the passed Group.
  showGroup = new EventEmitter<EmGroup>();

  mParents: BaseEntity[];

  constructor(
    private mImageService: ImageService,
    public mPropertyTypeService: PropertyTypeService,
    private mTagService: TagService,
    private mReaderService: ReaderService,
    private mDriverObjectService: DriverObjectService,
    private mObjectService: ObjectService,
    private mAntennaService: AntennaService,
    private mLocationService: LocationService,
    private mServiceService: ServiceService,
    private mServerObjectService: ServerObjectService,
    private mZoneService: ZoneService,
    private mIconUploadConfirmDialogService: IconUploadConfirmDialogService
  ) { }



  ngOnInit(): void {

  }



  public showDialog(): void {
    CanActivateAuthGuard.popupActive = true;

    //Reset the active Tab to the "information" Tab
    $('.nav-tabs a[href="#information"]').tab('show');

    //Show the Dialog
    $("#updateAntennaDiv").modal("show");
  }

  public hideDialog(): void {
    CanActivateAuthGuard.popupActive = false;

    //Hide the Dialog and reset the User being edited/created...
    $("#updateAntennaDiv").modal("hide");
  }

  isEditDialog(): boolean {
    return this.entity != null && this.entity.id != null;
  }

  isNewDialog(): boolean {
    return this.entity != null && this.entity.id == null;
  }



  public imageSource(): string {
    return this.mImageService.getImageSourceForEntity(this.entity);
  }

  public iconSource(): string {
    return this.mImageService.getIconSourceForEntity(this.entity);
  }



  isEntityType_Tag(): boolean {
    return this.entityType != null && this.entityType == GroupType.TAG;
  }

  isEntityType_Reader(): boolean {
    return this.entityType != null && this.entityType == GroupType.READER;
  }

  isEntityType_Location(): boolean {
    return this.entityType != null && this.entityType == GroupType.LOCATION;
  }

  isEntityType_Zone(): boolean {
    return this.entityType != null && this.entityType == GroupType.READZONE;
  }

  isEntityType_Antenna(): boolean {
    return this.entityType != null && this.entityType == GroupType.ANTENNA;
  }

  isEntityType_Service(): boolean {
    return this.entityType != null && this.entityType == GroupType.SERVICE;
  }

  isEntityType_ServerObject(): boolean {
    return this.entityType != null && this.entityType == GroupType.SERVEROBJECT;
  }

  changeImageClicked(): void {
    this.entityImageFileInput.nativeElement.click();
  }

  onImageFileChange(event: any): void {
    if (event.target.files.length === 1) {
      this.submitImage(event.target.files[0]);
    }
  }

  resetImage(): void {
    if (this.entity != null) {
      this.entity.image = null;
    }
  }

  submitImage(file: any): void {
    const formData = new FormData();
    formData.append("file", file);
    this.mImageService
      .uploadImage(formData)
      .then(
        image => {
          if (this.entity != null) {
            this.entity.image = image;
          }
          this.entityImageFileInput.nativeElement.value = "";
        },
        error => {
          this.entityImageFileInput.nativeElement.value = "";
          this.handleError(error);
        }
    );
  }





  changeIconClicked(): void {
    this.entityIconFileInput.nativeElement.click();
  }

  onIconFileChange(event: any): void {
    if (event.target.files.length === 1) {
      this.submitIcon(event.target.files[0]);
    }
  }

  resetIcon(): void {
    if (this.entity != null) {
      this.entity.icon = null;
    }
  }

  submitIcon(file: any): void {
    //KHT 08/24/2021 - added IconUploadConfirmDialog to confirm icon resize on upload
    this.mIconUploadConfirmDialogService.show().then((confirmed) => {
      if (confirmed) {
        console.log("Icon Upload Proceeding...")
        const formData = new FormData();
        formData.append("file", file);
        formData.append("isIcon", "true");//KHT 08/24/2021 - backend checks for 'isIcon' in the formData, and resizes the icon if it is too large
        this.mImageService
          .uploadImage(formData)
          .then(
            icon => {
              if (this.entity != null) {
                this.entity.icon = icon;
              }
              this.entityIconFileInput.nativeElement.value = "";
            },
            error => {
              this.entityIconFileInput.nativeElement.value = "";
              this.handleError(error);
            }
          );
      }
      else {
        console.log("Icon Upload Halted!")
      }
    }
    ).catch(() => console.log('User dismissed the dialog (e.g., by using ESC, clicking the cross icon, or clicking outside the dialog)'));
  }

  addNewParameter(): void {
    if (this.entity.properties == null) {
      this.entity.properties = [];
    }
    this.entity.properties.push(new Property());

  }

  removeParameter(property: Property): void {
    const index = this.entity.properties.indexOf(property);
    if (index > -1) {
      this.entity.properties.splice(index, 1);
    }
  }

  getPropertyTypes(): string[] {
    return Object.keys(PropertyType);
  }

  getParametersForPropertyType(propertyType: PropertyType): PropertyTypeParameter[] {
    return this.mPropertyTypeService.getParametersForPropertyType(propertyType);
  }





  createOrUpdateClicked(): void {
    this.createOrUpdateEvent.emit(true);
  }

  showGroupClicked(group: EmGroup): void {
    this.showGroup.emit(group);
  }

  onSelectParent(): void {
    console.log(this.entityType);
    if (this.entityParent) {
      if (this.isEntityType_Tag()) {
        this
          .mTagService
          .getTagDefinitionById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.isEntityType_Reader()) {
        this
          .mReaderService
          .getReaderById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.entityType == GroupType.DRIVEROBJECT) {
        this
          .mDriverObjectService
          .getDriverObjectById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.entityType == GroupType.OBJECT) {
        this
          .mObjectService
          .getObjectForId(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.entityType == GroupType.ANTENNA) {
        this
          .mAntennaService
          .getAntennaById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.entityType == GroupType.SERVICE) {
        this
          .mServiceService
          .getServiceById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.entityType == GroupType.SERVEROBJECT) {
        this
          .mServerObjectService
          .getServerObjectById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.entityType == GroupType.LOCATION) {
        this
          .mLocationService
          .getLocationById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      } else if (this.entityType == GroupType.READZONE) {
        console.log('Parent: ' + this.entityParent);
        this
          .mZoneService
          .getZoneById(this.entityParent.id)
          .then(
            response => {
              this.entity.properties = response.properties;
              this.entity.parent = response;
            },
            error => this.handleError(error)
          );
      }
    } else {
      console.log("No Parent for this Entity Type");
      this.entity.properties = null;
      this.entity.parent = null;
    }
  }

  handleError(error: any): void {
    console.error("EntityTypeDialogComponent.handleError() - Error: " + error);
    if (error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
  }

}
